/** NOTES:
 * - For performance optimisation we send server generated and DB cached css variables of the colors
 * - Only on e.g. safe via to_json() we update the color values.
 * - Colors are referenced by their persistent `id`.
 * - When the color set name is changed, the id remains the same.
 * - The color set id is used as the value e.g. for BlockColorTool.
 * - There must always be exactly one color set marked as `default: true`.
 */

/**
 * CSS Variables Output Example (for the default color set with id 'default'):
 *
 * // ID-Specific Variables
 * --color-default-background: oklch(100% 0 0);
 * --color-default-foreground: oklch(0% 0 0);
 * --color-default-emphasis-background: oklch(50% 0 0);
 * --color-default-emphasis-foreground: oklch(0% 0 0);
 * --color-default-background-lux-mode: 1;
 * --color-default-foreground-lux-mode: -1;
 * --color-default-emphasis-background-lux-mode: -1;
 * --color-default-emphasis-foreground-lux-mode: -1;
 * --color-default-font-smoothing: auto;  // 'antialiased' for dark backgrounds (< 0.5 L), 'auto' otherwise
 */

import { 
  nanoid, 
  parse_oklch,
  format_oklch, 
  oklch_to_rgb, 
  get_apca_contrast, 
  meets_contrast_requirements,
  get_contrast_level,
} from '#lib/util.js';
import { dev } from '$app/env';

export class ColorModel {
  /**
   * LCHA color object derived from OKLCH string - luminance, chroma, hue, alpha (all 0-1 except hue 0-360)
   * @typedef {{l: number, c: number, h: number, a: number }} LCHA
   */

  /**
   * OKLCH color string
   * @typedef {string} OKLCH_String - Color value as string (e.g. 'oklch(1 0 0 / 1)')
   */


  /**
   * @typedef {Object} color_set
   * @property {string} id - Unique persistent identifier.
   * @property {boolean} [default] - Whether this is the default color set (exactly one must be true).
   * @property {string} name - Display name for the color set (auto-generated or custom).
   * @property {string} [custom_name] - User-provided custom name (if set, overrides auto-generated name).
   * @property {OKLCH_String} background - Background color value.
   * @property {OKLCH_String} foreground - Foreground color value.
   * @property {OKLCH_String} emphasis_background - Emphasis background color value.
   */

  /**
   * Default color set structure
   * @type {Omit<color_set, 'id'>}
   */
  static #BASE_DEFAULT_COLOR_SET_STRUCTURE = {
    default: true,
    name: 'Default',
    background: 'oklch(1 0 0 / 1)', // white
    foreground: 'oklch(0 0 0 / 1)', // black
    emphasis_background: 'oklch(0.7 0.1949 236.12 / 1)', // blue
  };

  /** @type {OKLCH_String} */
  static #DEFAULT_OKLCH_WHITE = 'oklch(1 0 0 / 1)';
  /** @type {OKLCH_String} */
  static #DEFAULT_OKLCH_BLACK = 'oklch(0 0 0 / 1)';
  /** @type {OKLCH_String} */
  static #DEFAULT_OKLCH_EMPHASIS = 'oklch(0.7 0.1949 236.12 / 1)';

  /**
   * A list of all colors available for coloring blocks, custom colors may be added by the user.
   * Each color set has an id, name, background, foreground, and emphasis_background color.
   * The emphasis_foreground is computed automatically based on contrast.
   * @type {Array<color_set>}
   */
  site_colors = $state([]);

  /**
   * A map of color sets keyed by their ID for efficient lookups.
   * The values contain the parsed LCHA representations of the colors.
   * @type {Object<string, {name: string, default: boolean, background: LCHA, foreground: LCHA, emphasis_background: LCHA}>}
   */
  color_map = $derived(this.#compute_color_map());

  /**
   * The ID of the default color set.
   * @type {string | undefined}
   */
  default_color_id = $derived(this.site_colors.find(cs => cs.default)?.id);

  // Non-reactive CSS variables cached from the last save (to_json call)
  /** @type {string | undefined} */
  css_site_color_variables;


  constructor({ site_colors, css_site_color_variables } = {}) {
    let migrated_site_colors = [];

    // Helper to ensure color is a valid OKLCH string, converting from legacy LCHA if needed
    const ensure_oklch_string = (color) => {
      // 1. Already a valid OKLCH string?
      if (typeof color === 'string' && color.trim().toLowerCase().startsWith('oklch(')) {
        return color;
      }

      // 2. LCHA object? (LEGACY SUPPORT)
      if (typeof color === 'object' && color !== null && typeof color.l === 'number') {
        // START LEGACY BLOCK: Handling old LCHA object format
        // Normalize LCHA object before formatting
        const lcha_to_format = { ...color }; // Copy to avoid mutation

        // Check if l is likely 0-100 range and normalize to 0-1
        if (lcha_to_format.l > 1) { 
          lcha_to_format.l /= 100;
        }
        // Also check/normalize alpha if present and > 1 (might be 0-100)
        if (lcha_to_format.a !== undefined && lcha_to_format.a !== null && lcha_to_format.a > 1) {
           lcha_to_format.a /= 100;
        } 
        // Ensure alpha is clamped 0-1 even if it was already in range
        if (lcha_to_format.a !== undefined && lcha_to_format.a !== null) {
            lcha_to_format.a = Math.max(0, Math.min(1, lcha_to_format.a));
        }
        
        // Ensure L is clamped 0-1
        lcha_to_format.l = Math.max(0, Math.min(1, lcha_to_format.l));

        try {
          // Format the normalized object into the desired string format
          return format_oklch(lcha_to_format); 
        } catch (e) { 
           console.error("Failed to format LCHA object during migration:", color, e);
           // Fallback if formatting fails
           return ColorModel.#DEFAULT_OKLCH_WHITE; 
        }
        // END LEGACY BLOCK: Handling old LCHA object format
      }

      // 3. Fallback for other invalid types or failed formatting
      console.warn("Invalid color format encountered during migration, using default white:", color);
      return ColorModel.#DEFAULT_OKLCH_WHITE; 
    };

    // --- Migration Logic ---  
    // START LEGACY BLOCK: Handling old object-based site_colors format
    if (site_colors && !Array.isArray(site_colors)) {
        // --- Old object format migration (pre-array, likely used fill/body/control/slug) ---
        console.warn("Migrating site_colors from legacy object format."); // Log migration
        const old_colors = { ...site_colors }; // Clone to avoid modifying input
        const known_keys = ['canvas', 'accent_1', 'accent_2']; // Example legacy keys

        known_keys.forEach((key, index) => {
            if (old_colors.hasOwnProperty(key)) {
                const old_color_set = old_colors[key] || {};
                const is_default = key === 'canvas'; // Assume 'canvas' was the default in this format

                migrated_site_colors.push({
                    id: old_color_set.id || nanoid(), // Use old ID if present, else generate new
                    default: is_default,
                    name: old_color_set.name || key, // Use old name or the key itself
                    // Attempt to map old properties, ensure format
                    background: ensure_oklch_string(old_color_set.background || old_color_set.fill || ColorModel.#DEFAULT_OKLCH_WHITE),
                    foreground: ensure_oklch_string(old_color_set.foreground || old_color_set.body || ColorModel.#DEFAULT_OKLCH_BLACK),
                    emphasis_background: ensure_oklch_string(old_color_set.emphasis_background || old_color_set.control || ColorModel.#DEFAULT_OKLCH_EMPHASIS),
                });
                delete old_colors[key]; // Remove migrated key
            }
        });

        // Handle any remaining keys in the old object as additional color sets
        Object.entries(old_colors).forEach(([key, old_color_set]) => {
             if (typeof old_color_set === 'object' && old_color_set !== null) {
                 migrated_site_colors.push({
                     id: old_color_set.id || nanoid(),
                     default: false, // Cannot be default
                     name: old_color_set.name || key,
                     background: ensure_oklch_string(old_color_set.background || old_color_set.fill || ColorModel.#DEFAULT_OKLCH_WHITE),
                     foreground: ensure_oklch_string(old_color_set.foreground || old_color_set.body || ColorModel.#DEFAULT_OKLCH_BLACK),
                     emphasis_background: ensure_oklch_string(old_color_set.emphasis_background || old_color_set.control || ColorModel.#DEFAULT_OKLCH_EMPHASIS),
                 });
             }
         });
    // END LEGACY BLOCK: Handling old object-based site_colors format
    } else if (Array.isArray(site_colors) && site_colors.length > 0) {
        // --- Array format migration (might lack id, default, or use LCHA objects) ---
        migrated_site_colors = site_colors.map((color_set, index) => {
            const result = {
                // START LEGACY BLOCK: Ensure ID exists
                id: color_set.id || nanoid(), // Assign ID if missing
                // END LEGACY BLOCK: Ensure ID exists
                
                // Preserve the default flag if it exists in the input array
                default: color_set.default === true, 

                name: color_set.name || `Color ${index + 1}`, // Provide name if missing
                
                // Ensure colors are OKLCH strings, potentially converting from LCHA
                background: ensure_oklch_string(color_set.background || ColorModel.#DEFAULT_OKLCH_WHITE),
                foreground: ensure_oklch_string(color_set.foreground || ColorModel.#DEFAULT_OKLCH_BLACK),
                
                // START LEGACY BLOCK: Handle old 'emphasis' key
                // Handle old 'emphasis' key if present, otherwise use emphasis_background
                emphasis_background: ensure_oklch_string(color_set.emphasis_background || color_set.emphasis || ColorModel.#DEFAULT_OKLCH_EMPHASIS),
                // END LEGACY BLOCK: Handle old 'emphasis' key
            };
            
            // Preserve custom_name if it was explicitly set by user
            if (color_set.custom_name) {
                result.custom_name = color_set.custom_name;
            }
            
            return result;
        });
    }

    // --- Ensure at least one color set exists --- 
    if (migrated_site_colors.length === 0) {
        // This case handles initial setup or completely empty legacy data
        migrated_site_colors.push({ id: nanoid(), ...ColorModel.#BASE_DEFAULT_COLOR_SET_STRUCTURE });
    }

    // --- Ensure exactly one default color set (Handles legacy default assumptions) --- 
    // START LEGACY BLOCK: Consolidating default flag from various old states
    let default_count = migrated_site_colors.filter(cs => cs.default === true).length;

    if (default_count === 0) {
       // If no default was found after mapping/migration, mark the first one.
       if (migrated_site_colors.length > 0) {
           migrated_site_colors[0].default = true;
       } 
    } else if (default_count > 1) {
        // If multiple defaults were found (e.g., from bad data), keep only the first one found.
        let first_default_found = false;
        migrated_site_colors.forEach(cs => {
            if (cs.default === true) {
                if (!first_default_found) {
                    first_default_found = true;
                } else {
                    cs.default = false; // Unset subsequent defaults
                }
            }
        });
    }
    // END LEGACY BLOCK: Consolidating default flag

    // START LEGACY BLOCK: Remove old properties explicitly
    // Remove legacy properties like 'slug' or 'emphasis' after migration
    migrated_site_colors.forEach(cs => {
      delete cs.slug;
      delete cs.emphasis;
      // Could also delete old LCHA properties if they existed alongside strings, e.g.:
      // delete cs.fill; delete cs.body; delete cs.control;
    });
    // END LEGACY BLOCK: Remove old properties

    // Assign the processed colors
    this.site_colors = migrated_site_colors;
    this.css_site_color_variables = css_site_color_variables;
  }

  to_json() {
    // Ensure compute is called on the *current* reactive state before snapshotting
    const computed_css = this.compute_css_site_color_variables();
    return {
      // Snapshot the reactive array
      site_colors: $state.snapshot(this.site_colors),
      // Cache css variables
      css_site_color_variables: computed_css,
    };
  }

  // Alias for JSON.stringify compatibility
  toJSON() {
    return this.to_json();
  }

  /**========================================================================
   *
   *                           PRIVATE HELPERS
   *
   *========================================================================**/

  /**
   * Computes the color map for efficient lookups.
   * Parses OKLCH strings into LCHA objects.
   * @private
   */
  #compute_color_map() {
    const current_site_colors = $state.snapshot(this.site_colors); // Use snapshot for calculation
    const map = {};
    current_site_colors.forEach(cs => {
        // Attempt to parse colors, use defaults if parsing fails
        const background_lcha = parse_oklch(cs.background) || parse_oklch(ColorModel.#DEFAULT_OKLCH_WHITE);
        const foreground_lcha = parse_oklch(cs.foreground) || parse_oklch(ColorModel.#DEFAULT_OKLCH_BLACK);
        const emphasis_background_lcha = parse_oklch(cs.emphasis_background) || parse_oklch(ColorModel.#DEFAULT_OKLCH_EMPHASIS);

        map[cs.id] = {
            name: cs.name,
            default: cs.default || false,
            background: background_lcha,
            foreground: foreground_lcha,
            emphasis_background: emphasis_background_lcha
        };
    });
    return map;
  }


  /**
   * Generates the CSS variables based on the colors defined in the `site_colors` array.
   * Uses the persistent ID for variable names for non-default sets, and 'default' for the default set.
   * Includes generic variables mapped to the default color set's variables.
   * This method is reactive to changes in `site_colors` when called within a reactive context (like $derived).
   * @returns {string} The CSS variables as a string.
   */
   compute_css_site_color_variables() {
    const current_site_colors = $state.snapshot(this.site_colors); // Use snapshot
    let id_specific_variables = '';
    let generic_variables = '';
    let default_set_found = false;

    // --- Generate ID-specific variables --- 
    current_site_colors.forEach(color_set => {
      if (!color_set || !color_set.id) return; // Skip invalid entries

      // Use 'default' as the CSS identifier if this is the default set, otherwise use its ID
      const id_css = color_set.default === true ? 'default' : color_set.id;

      // Parse colors from strings, falling back to defaults if invalid
      let background = parse_oklch(color_set.background) || parse_oklch(ColorModel.#DEFAULT_OKLCH_WHITE);
      let foreground = parse_oklch(color_set.foreground) || parse_oklch(ColorModel.#DEFAULT_OKLCH_BLACK);
      let emphasis_background = parse_oklch(color_set.emphasis_background) || parse_oklch(ColorModel.#DEFAULT_OKLCH_EMPHASIS);

      if (!background || !foreground || !emphasis_background) {
        console.warn(`Could not parse colors for set ID ${color_set.id}. Using defaults.`);
        // Assign default parsed values if any failed
        if (!background) background = parse_oklch(ColorModel.#DEFAULT_OKLCH_WHITE);
        if (!foreground) foreground = parse_oklch(ColorModel.#DEFAULT_OKLCH_BLACK);
        if (!emphasis_background) emphasis_background = parse_oklch(ColorModel.#DEFAULT_OKLCH_EMPHASIS);
      }

      // --- Compute emphasis_foreground based on APCA contrast --- 
      const emphasis_bg_rgb = oklch_to_rgb(
          emphasis_background.l,
          emphasis_background.c,
          emphasis_background.h
      );
      const foreground_rgb = oklch_to_rgb(
          foreground.l,
          foreground.c,
          foreground.h
      );
      const background_rgb = oklch_to_rgb(
          background.l,
          background.c,
          background.h
      );

      const contrast_with_foreground = get_apca_contrast(foreground_rgb, emphasis_bg_rgb);
      const contrast_with_background = get_apca_contrast(background_rgb, emphasis_bg_rgb);

      // Choose the color that provides better contrast against the emphasis background
      const computed_emphasis_foreground = Math.abs(contrast_with_foreground) > Math.abs(contrast_with_background)
          ? foreground
          : background;

      // Check if the chosen color meets minimum requirements
      // const chosen_contrast = Math.abs(contrast_with_foreground) > Math.abs(contrast_with_background)
      //     ? contrast_with_foreground
      //     : contrast_with_background;

      // TODO: use actual font weight of the font used for the control text style
      // TODO: Display result of meets_contrast_requirements in UI of color set
      // if (!meets_contrast_requirements(chosen_contrast, 400) && dev) {
      //   console.warn(`
      //     Emphasis text contrast may be insufficient for readability. 
      //     Color set: ${color_set.id}
      //     Contrast: ${chosen_contrast.toFixed(2)} (${get_contrast_level(chosen_contrast, 400)})
      //     Background: ${color_set.background}
      //     Foreground: ${color_set.foreground}
      //     Emphasis background: ${color_set.emphasis_background}
      //     Computed emphasis foreground: ${format_oklch(computed_emphasis_foreground)}
      //     Font weight: 400
      //   `);
      // }
      // -------------------------------------------------------------

      // Helper to get lux mode (1 for light >= 0.5 L, -1 for dark < 0.5 L)
      const get_lux_mode = (lcha) => (lcha && typeof lcha.l === 'number' && lcha.l >= 0.5) ? 1 : -1;
      // Helper for font smoothing ('antialiased' for very bright foregrounds)
      const get_font_smoothing = (lcha) => (lcha && typeof lcha.l === 'number' && lcha.l > 0.8) ? 'antialiased' : 'auto';

      // Append ID-specific CSS variables (using id_css)
      id_specific_variables += `--color-${id_css}-background: ${color_set.background};`;
      id_specific_variables += `--color-${id_css}-foreground: ${color_set.foreground};`;
      id_specific_variables += `--color-${id_css}-emphasis-background: ${color_set.emphasis_background};`;
      id_specific_variables += `--color-${id_css}-emphasis-foreground: ${format_oklch(computed_emphasis_foreground)};`; // Computed value
      
      id_specific_variables += `--color-${id_css}-background-lux-mode: ${get_lux_mode(background)};`;
      id_specific_variables += `--color-${id_css}-foreground-lux-mode: ${get_lux_mode(foreground)};`;
      id_specific_variables += `--color-${id_css}-emphasis-background-lux-mode: ${get_lux_mode(emphasis_background)};`;
      id_specific_variables += `--color-${id_css}-emphasis-foreground-lux-mode: ${get_lux_mode(computed_emphasis_foreground)};`;
      id_specific_variables += `--${id_css}-font-smoothing: ${get_font_smoothing(foreground)};`;

      // Track if we found the default set
      if (color_set.default === true) {
        default_set_found = true;
      }
    });

    // --- Generate generic variables mapping to the default set --- 
    if (default_set_found) {
      generic_variables += `--color-background: var(--color-default-background);`;
      generic_variables += `--color-foreground: var(--color-default-foreground);`;
      generic_variables += `--color-emphasis-background: var(--color-default-emphasis-background);`;
      generic_variables += `--color-emphasis-foreground: var(--color-default-emphasis-foreground);`;
      generic_variables += `--color-background-lux-mode: var(--color-default-background-lux-mode);`;
      generic_variables += `--color-foreground-lux-mode: var(--color-default-foreground-lux-mode);`;
      generic_variables += `--color-emphasis-background-lux-mode: var(--color-default-emphasis-background-lux-mode);`;
      generic_variables += `--color-emphasis-foreground-lux-mode: var(--color-default-emphasis-foreground-lux-mode);`;
      generic_variables += `--font-smoothing: var(--default-font-smoothing);`;
    } else {
      console.warn("No default color set found. Generic CSS variables will not be generated.");
      // Optionally generate fallbacks here if needed
    }

    return id_specific_variables + generic_variables;
  }

  /**========================================================================
   *
   *                            PUBLIC METHODS
   *
   *========================================================================**/

  /**
   * Adds a new color set with default values.
   * @param {Partial<Omit<color_set, 'id' | 'default'>>} [initial_values] - Optional initial values for the new set.
   * @returns {string} The ID of the newly created color set.
   */
  add_color_set = (initial_values = {}) => {
    const new_id = nanoid();
    const new_set = {
      id: new_id,
      default: false, // New sets are never default initially
      name: initial_values.name || `New Color ${this.site_colors.length + 1}`,
      background: initial_values.background || ColorModel.#DEFAULT_OKLCH_WHITE,
      foreground: initial_values.foreground || ColorModel.#DEFAULT_OKLCH_BLACK,
      emphasis_background: initial_values.emphasis_background || ColorModel.#DEFAULT_OKLCH_EMPHASIS,
    };
    // Preserve custom_name if provided
    if (initial_values.custom_name) {
      new_set.custom_name = initial_values.custom_name;
    }
    // Add to the reactive array
    this.site_colors = [...this.site_colors, new_set];
    return new_id;
  }

  /**
   * Reorders color sets by moving an item from one index to another.
   * @param {number} from_index - The current index of the item to move.
   * @param {number} to_index - The target index to move the item to.
   */
  reorder_color_sets(from_index, to_index) {
    if (from_index === to_index) return;
    if (from_index < 0 || from_index >= this.site_colors.length) return;
    if (to_index < 0 || to_index >= this.site_colors.length) return;
    
    const new_colors = [...this.site_colors];
    const [moved] = new_colors.splice(from_index, 1);
    new_colors.splice(to_index, 0, moved);
    this.site_colors = new_colors;
  }

  /**
   * Deletes a color set by its ID.
   * Prevents deletion of the default color set.
   * @param {string} id - The ID of the color set to delete.
   */
  delete_color_set(id) {
    const index_to_delete = this.site_colors.findIndex(cs => cs.id === id);
    if (index_to_delete === -1) return; // Not found

    // Prevent deletion of the default set
    if (this.site_colors[index_to_delete].default === true) {
      console.warn("Cannot delete the default color set.");
      // Optionally show an alert or notification to the user here
      alert("Cannot delete the default color set.");
      return;
    }

    // Filter out the color set
    this.site_colors = this.site_colors.filter(cs => cs.id !== id);
  }

  /**
   * Sets a specific color set as the default by moving it to the first position.
   * The first item in the array is always considered the default.
   * @param {string} id - The ID of the color set to mark as default.
   */
  set_default_color_set(id) {
    const current_index = this.site_colors.findIndex(cs => cs.id === id);
    if (current_index === -1) {
       console.warn(`Cannot set default: Color set with ID ${id} not found.`);
       return;
    }
    
    if (current_index === 0) return; // Already default
    
    // Move to first position
    const new_colors = [...this.site_colors];
    const [moved] = new_colors.splice(current_index, 1);
    new_colors.unshift(moved);
    
    // Update default flags: first item is default
    this.site_colors = new_colors.map((cs, index) => ({
      ...cs,
      default: index === 0
    }));
  }
  
  /**
   * Ensures the first item has default: true and others have default: false.
   * Call this after any reordering operation.
   */
  sync_default_flag() {
    const needs_update = this.site_colors.some((cs, index) => 
      (index === 0 && !cs.default) || (index > 0 && cs.default)
    );
    
    if (needs_update) {
      this.site_colors = this.site_colors.map((cs, index) => ({
        ...cs,
        default: index === 0
      }));
    }
  }

  /**
   * Updates the properties of a specific color set.
   * @param {string} id - The ID of the color set to update.
   * @param {Partial<Omit<color_set, 'id' | 'default'>>} updates - The properties to update.
   */
  update_color_set(id, updates) {
      this.site_colors = this.site_colors.map(cs => {
          if (cs.id === id) {
              // Create a new object with updated values, preserving id and default status
              return { 
                ...cs, // Keep existing id and default status
                ...updates // Apply updates (name, background, foreground, emphasis_background)
               };
          }
          return cs; // Return unchanged set
      });
  }

  /**
   * Finds a color set by its ID.
   * @param {string} id - The ID to search for.
   * @returns {color_set | undefined} The found color set or undefined.
   */
  find_color_set_by_id(id) {
    return this.site_colors.find(cs => cs.id === id);
  }
}
